/**
 * Sidebar + tab labels + key page headings (EN / AR).
 * Dot paths: e.g. getNavT(lang)('sections.orders') or getNavT(lang)('orders.createOrder').
 */

import { REPORT_PATH_LABELS } from '../components/Reports/reportRouteMeta';

function resolveDot(root, dotPath) {
  if (!root || !dotPath) return undefined;
  const parts = dotPath.split('.');
  let cur = root;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

/** Arabic labels for report tab paths (fallback to English from REPORT_PATH_LABELS if missing). */
const REPORT_PATH_AR = {
  '/home/reports/order-reports': 'تقارير الطلبات',
  '/home/reports/order-report': 'تقرير الطلبات',
  '/home/reports/pending-orders': 'الطلبات المعلقة',
  '/home/reports/advance-orders': 'الطلبات المسبقة',
  '/home/reports/pickup-reports': 'تقارير الاستلام',
  '/home/reports/delivery-reports': 'تقارير التسليم',
  '/home/reports/delivery-report': 'تقرير التسليم',
  '/home/reports/shipment-reports': 'تقارير الشحن',
  '/home/reports/shipment-report': 'تقرير الشحن',
  '/home/reports/shipment-expense-report': 'تقرير مصاريف الشحن',
  '/home/reports/clients-reports': 'تقارير العملاء',
  '/home/reports/inactive-clients': 'العملاء غير النشطين',
  '/home/reports/client-orders': 'الطلبات حسب العميل',
  '/home/reports/customer-orders': 'الطلبات حسب الزبون',
  '/home/reports/customer-reports': 'تقارير الزبائن',
  '/home/reports/employee-reports': 'تقارير الموظفين',
  '/home/reports/driver-commission': 'عمولة السائق',
  '/home/reports/employee-orders': 'الطلبات حسب الموظف',
  '/home/reports/driver-expense': 'مصاريف السائق',
  '/home/reports/agent-reports': 'تقارير الوكلاء',
  '/home/reports/agent-orders': 'الطلبات حسب الوكيل',
  '/home/reports/expense-reports': 'تقارير المصاريف',
  '/home/reports/company-expense': 'مصاريف الشركة',
  '/home/reports/payment-reports': 'تقارير الدفع',
  '/home/reports/client-payment-report': 'تقرير دفعات العملاء',
  '/home/reports/return-reports': 'تقارير المرتجعات',
  '/home/reports/client-return-report': 'تقرير مرتجعات العملاء',
  '/home/reports/financial-reports': 'التقارير المالية',
  '/home/reports/income-expense': 'الإيرادات والمصاريف',
  '/home/reports/vat-report': 'تقرير ضريبة القيمة المضافة',
};

const bundles = {
  en: {
    sections: {
      home: 'Home',
      masters: 'Masters',
      accounts: 'Accounts',
      orders: 'Orders',
      messages: 'Messages',
      uploads: 'Uploads',
      pickup: 'Pickup',
      delivery: 'Delivery',
      shipments: 'Shipments',
      payments: 'Payments',
      returns: 'Returns',
      followup: 'Followup',
      expenses: 'Expenses',
      approvals: 'Approvals',
      tracking: 'Tracking',
      vehicleManagement: 'Vehicle',
      reports: 'Reports',
      admin: 'Admin',
      accounting: 'Accounting',
      counters: 'Counters',
      store: 'Store',
      customization: 'Customization',
    },
    home: {
      dashboard: 'Dashboard',
      dashboards: 'Dashboards',
      vehicleDashboard: 'Vehicle dashboard',
    },
    brand: { title: 'Ainsoft Delivery', subtitle: 'Office Portal' },
    logout: 'Logout',
    masters: {
      emirates: 'Emirates',
      areas: 'Areas',
      locations: 'Locations',
      expenses: 'Expenses',
      reasons: 'Reasons',
      types: 'Types',
      vehicles: 'Vehicles',
      designations: 'Designations',
    },
    tracking: {
      voucher: 'Track By Voucher',
      bill: 'Track By Bill',
      customer: 'Track By Customer',
      driver: 'Driver Tracking',
      activityTracker: 'Activity Tracker',
    },
    accounts: {
      employees: 'Employees',
      clients: 'Clients',
      customers: 'Customers',
      agents: 'Agents',
      users: 'Users',
      addEmployee: 'Add Employee',
      addClient: 'Add Client',
      addCustomer: 'Add Customer',
      addAgent: 'Add Agent',
    },
    vehicle: {
      list: 'Vehicle List',
      assignments: 'Assignments',
      maintenance: 'Maintenance',
      fuelLogs: 'Fuel logs',
      documents: 'Documents',
      insurance: 'Insurance',
      permits: 'Permits',
      inspections: 'Inspections',
      expenses: 'Vehicle expenses',
      odometerLogs: 'Odometer logs',
      accidents: 'Accidents',
      serviceHistory: 'Service history',
      fineRecords: 'Fine records',
      addEdit: 'Add Vehicle',
      addMaintenance: 'Add Maintenance',
      addDocument: 'Add Document',
      addInsurance: 'Add Insurance',
      addAssignment: 'Add Assignment',
      addFine: 'Add Fine',
    },
    uploads: {
      orderUpload: 'Order Upload',
      locationUpload: 'Location Upload',
      clientUpload: 'Client Upload',
    },
    messages: {
      draftEmails: 'Draft Emails',
      draftSms: 'Draft SMS',
      deliveryNoteSms: 'Delivery Note SMS',
    },
    orders: {
      createOrder: 'Create Order',
      orderList: 'Order List',
      orderCalendar: 'Orders Calendar',
      todaysOrders: "Today's Orders",
      holdOrders: 'Hold Orders',
      reverseCancel: 'Reverse Cancel',
      batchPrint: 'Batch Print',
    },
    admin: {
      orderEdit: 'Order Edit',
      orderPaymentEdit: 'Order Payment Edit',
      orderStatusEdit: 'Order Status Edit',
      orderDateEdit: 'Order Date Edit',
      orderLocationEdit: 'Order Location Edit',
      orderAdjustment: 'Order Adjustment',
    },
    accounting: {
      accountTypes: 'Account Type',
      chartOfAccounts: 'Chart Of Accounts',
      cpv: 'Cash Payment Voucher',
      crv: 'Cash Receipt Voucher',
      fpv: 'Fund Payment Voucher',
      frv: 'Fund Receipt Voucher',
      chequePv: 'Cheque Payment Voucher',
      chequeRv: 'Cheque Receipt Voucher',
      pendingPdc: 'Pending PDC',
      processedPdc: 'Processed PDC',
      generalLedger: 'General Ledger',
      trialBalance: 'Trial Balance',
      balanceSheet: 'Balance Sheet',
      profitLoss: 'Profit & Loss',
      newVoucher: 'New Voucher',
      editVoucher: 'Edit Voucher',
    },
    followup: {
      deliveryOrder: 'Delivery Order Followup',
      pickupOrder: 'Pickup Order Followup',
      driver: 'Driver Followup',
      payment: 'Payment Followup',
      return: 'Return Followup',
    },
    expenses: {
      driver: 'Driver Expense',
      company: 'Company Expense',
    },
    pickup: {
      allocation: 'Pickup Allocation',
      notes: 'Pickup Notes',
      status: 'Pickup Status',
      statistics: 'Pickup Statistics',
      exchange: 'Pickup Exchange',
      pushPickup: 'Push Pickup',
    },
    delivery: {
      allocation: 'Delivery Allocation',
      notes: 'Delivery Notes',
      statistics: 'Delivery Statistics',
      exchange: 'Delivery Exchange',
      pushOrder: 'Push Order',
    },
    shipments: {
      newShipment: 'New Shipment',
      shipmentList: 'Shipment List',
      convertShipment: 'Convert Shipment',
    },
    payments: {
      createPayment: 'Create Payment',
      clientPayments: 'Client Payments',
      pendingPayments: 'Pending Payments',
    },
    returns: {
      createReturn: 'Create Return',
      clientReturns: 'Client Returns',
      pendingReturns: 'Pending Returns',
      clientReturnStatus: 'Client Return Status',
    },
    approvals: {
      shipment: 'Shipment Approval',
      payment: 'Payment Approvals',
      returns: 'Return Approvals',
    },
    store: {
      inScan: 'In Scan',
      outScan: 'Out Scan',
      deliveryScan: 'Delivery Scan',
      bulkInScan: 'Bulk In Scan',
      bulkOutScan: 'Bulk Out Scan',
      pickupScan: 'Pickup Scan',
    },
    counters: {
      counterNos: 'Counter Nos',
      changeStoreRef: 'Change Store Ref',
      areaStoreNoLock: 'Area Store No Lock',
      updateStoreRefNo: 'Update Store Ref No',
    },
    customization: {
      driverCommission: 'Driver Commission',
      clientAreaCharges: 'Client Area Charges',
      voucherBook: 'Voucher Book',
      smsConfiguration: 'SMS Configuration',
      whatsappConfiguration: 'Whatsapp Configuration',
      documentNumber: 'Document Number',
      status: 'Status',
      userPageRole: 'User Page Role',
      printHeader: 'Print Header',
    },
    user: {
      menuTitle: 'User menu',
      profile: 'User Profile',
      companyProfile: 'Company Profile',
      changePassword: 'Change Password',
      licenseInfo: 'License Info',
    },
    page: {
      dashboard: 'Dashboard',
      vehicleDashboard: 'Vehicle dashboard',
      createOrderHeading: 'Order Entry',
      createOrderSub: 'Create and manage orders',
      orderList: 'Order List',
      adminOrderEdit: 'Order Edit',
      adminOrderPaymentEdit: 'Order Payment Edit',
      adminOrderStatusEdit: 'Order Status Edit',
      adminOrderDateEdit: 'Order Date Edit',
      adminOrderLocationEdit: 'Order Location Edit',
      adminOrderAdjustment: 'Order Adjustment',
      todaysOrders: "Today's Orders",
      deliveryAllocation: 'Delivery Allocation',
      deliveryAllocationAllocate: 'Allocate Orders',
      deliveryNotes: 'Delivery Notes',
      deliveryStatistics: 'Delivery Statistics',
      deliveryExchange: 'Delivery Exchange',
      pushOrder: 'Push Order',
      pushOrderSub: 'Push orders to delivery notes',
      pickupAllocation: 'Pickup Allocation',
      pickupAllocationAllocate: 'Allocate Orders',
      pickupNotes: 'Pickup Notes',
      pickupStatus: 'Pickup Status',
      pickupStatistics: 'Pickup Statistics',
      pickupExchange: 'Pickup Exchange',
      pushPickup: 'Push Pickup',
      pushPickupSub: 'Push orders to pickup notes',
      pickupNote: 'Pickup Note',
      newShipment: 'New Shipment',
      shipmentList: 'Shipment List',
      createPayment: 'Create Payment',
      clientPayments: 'Client Payments',
      pendingPayments: 'Pending Payments',
      clientPaymentStatus: 'Client Payment Status',
      createReturn: 'Create Return',
      clientReturns: 'Client Returns',
      pendingReturns: 'Pending Returns',
      clientReturnStatus: 'Client Return Status',
      shipmentApprovals: 'Shipment Approval',
      paymentApprovalsList: 'Payment Approvals',
      returnApprovalsList: 'Return Approvals',
      deliveryNote: 'Delivery Note',
      newRef: 'New',
    },
    tabs: {
      orderDetails: 'Order Details',
      editEmployee: 'Edit Employee',
      editClient: 'Edit Client',
      editCustomer: 'Edit Customer',
      editAgent: 'Edit Agent',
      editVehicle: 'Edit Vehicle',
      vehicleDetails: 'Vehicle Details',
      editMaintenance: 'Edit Maintenance',
      editDocument: 'Edit Document',
      editInsurance: 'Edit Insurance',
      editAssignment: 'Edit Assignment',
      editFine: 'Edit Fine',
      vehicleFuelLogs: 'Vehicle Fuel Logs',
      vehiclePermits: 'Vehicle Permits',
      vehicleInspections: 'Vehicle Inspections',
      vehicleExpenses: 'Vehicle Expenses',
      vehicleOdometerLogs: 'Vehicle Odometer Logs',
      vehicleAccidents: 'Vehicle Accidents',
      vehicleServiceHistory: 'Vehicle Service History',
    },
  },
  ar: {
    sections: {
      home: 'الرئيسية',
      masters: 'البيانات الأساسية',
      accounts: 'الحسابات',
      orders: 'الطلبات',
      messages: 'الرسائل',
      uploads: 'الرفع',
      pickup: 'الاستلام',
      delivery: 'التسليم',
      shipments: 'الشحنات',
      payments: 'المدفوعات',
      returns: 'المرتجعات',
      followup: 'المتابعة',
      expenses: 'المصاريف',
      approvals: 'الموافقات',
      tracking: 'التتبع',
      vehicleManagement: 'المركبات',
      reports: 'التقارير',
      admin: 'الإدارة',
      accounting: 'المحاسبة',
      counters: 'العدادات',
      store: 'المستودع',
      customization: 'التخصيص',
    },
    home: {
      dashboard: 'لوحة التحكم',
      dashboards: 'لوحات البيانات',
      vehicleDashboard: 'لوحة المركبات',
    },
    brand: { title: 'أينسوفت للتوصيل', subtitle: 'بوابة المكتب' },
    logout: 'تسجيل الخروج',
    masters: {
      emirates: 'الإمارات',
      areas: 'المناطق',
      locations: 'المواقع',
      expenses: 'المصاريف',
      reasons: 'الأسباب',
      types: 'الأنواع',
      vehicles: 'المركبات',
      designations: 'المسميات الوظيفية',
    },
    tracking: {
      voucher: 'تتبع بالسند',
      bill: 'تتبع بالفاتورة',
      customer: 'تتبع بالعميل',
      driver: 'تتبع السائقين',
      activityTracker: 'سجل النشاط',
    },
    accounts: {
      employees: 'الموظفون',
      clients: 'العملاء',
      customers: 'الزبائن',
      agents: 'الوكلاء',
      users: 'المستخدمون',
      addEmployee: 'إضافة موظف',
      addClient: 'إضافة عميل',
      addCustomer: 'إضافة زبون',
      addAgent: 'إضافة وكيل',
    },
    vehicle: {
      list: 'قائمة المركبات',
      assignments: 'التعيينات',
      maintenance: 'الصيانة',
      fuelLogs: 'سجل الوقود',
      documents: 'المستندات',
      insurance: 'التأمين',
      permits: 'التصاريح',
      inspections: 'الفحوصات',
      expenses: 'مصاريف المركبة',
      odometerLogs: 'عداد المسافة',
      accidents: 'الحوادث',
      serviceHistory: 'سجل الخدمة',
      fineRecords: 'المخالفات',
      addEdit: 'إضافة مركبة',
      addMaintenance: 'إضافة صيانة',
      addDocument: 'إضافة مستند',
      addInsurance: 'إضافة تأمين',
      addAssignment: 'إضافة تعيين',
      addFine: 'إضافة مخالفة',
    },
    uploads: {
      orderUpload: 'رفع الطلبات',
      locationUpload: 'رفع المواقع',
      clientUpload: 'رفع العملاء',
    },
    messages: {
      draftEmails: 'مسودات البريد',
      draftSms: 'مسودات الرسائل',
      deliveryNoteSms: 'رسائل إيصال التسليم',
    },
    orders: {
      createOrder: 'إنشاء طلب',
      orderList: 'قائمة الطلبات',
      orderCalendar: 'تقويم الطلبات',
      todaysOrders: 'طلبات اليوم',
      holdOrders: 'الطلبات المعلقة',
      reverseCancel: 'عكس الإلغاء',
      batchPrint: 'طباعة الدفعة',
    },
    admin: {
      orderEdit: 'تعديل الطلب',
      orderPaymentEdit: 'تعديل دفع الطلب',
      orderStatusEdit: 'تعديل حالة الطلب',
      orderDateEdit: 'تعديل تاريخ الطلب',
      orderLocationEdit: 'تعديل موقع الطلب',
      orderAdjustment: 'تعديل الطلبات',
    },
    accounting: {
      accountTypes: 'نوع الحساب',
      chartOfAccounts: 'دليل الحسابات',
      cpv: 'دفع نقدي',
      crv: 'قبض نقدي',
      fpv: 'دفع صندوق',
      frv: 'قبض صندوق',
      chequePv: 'دفع شيك',
      chequeRv: 'قبض شيك',
      pendingPdc: 'شيكات مؤجلة معلقة',
      processedPdc: 'شيكات مؤجلة معالجة',
      generalLedger: 'دفتر الأستاذ العام',
      trialBalance: 'ميزان المراجعة',
      balanceSheet: 'الميزانية العمومية',
      profitLoss: 'الأرباح والخسائر',
      newVoucher: 'سند جديد',
      editVoucher: 'تعديل السند',
    },
    followup: {
      deliveryOrder: 'متابعة طلبات التسليم',
      pickupOrder: 'متابعة طلبات الاستلام',
      driver: 'متابعة السائق',
      payment: 'متابعة الدفع',
      return: 'متابعة المرتجع',
    },
    expenses: {
      driver: 'مصروف السائق',
      company: 'مصروف الشركة',
    },
    pickup: {
      allocation: 'توزيع الاستلام',
      notes: 'مذكرات الاستلام',
      status: 'حالة الاستلام',
      statistics: 'إحصائيات الاستلام',
      exchange: 'تبديل الاستلام',
      pushPickup: 'دفع الاستلام',
    },
    delivery: {
      allocation: 'توزيع التسليم',
      notes: 'مذكرات التسليم',
      statistics: 'إحصائيات التسليم',
      exchange: 'تبديل التسليم',
      pushOrder: 'دفع الطلب',
    },
    shipments: {
      newShipment: 'شحنة جديدة',
      shipmentList: 'قائمة الشحنات',
      convertShipment: 'تحويل شحنة',
    },
    payments: {
      createPayment: 'إنشاء دفعة',
      clientPayments: 'مدفوعات العملاء',
      pendingPayments: 'المدفوعات المعلقة',
    },
    returns: {
      createReturn: 'إنشاء مرتجع',
      clientReturns: 'مرتجعات العملاء',
      pendingReturns: 'المرتجعات المعلقة',
      clientReturnStatus: 'حالة مرتجع العميل',
    },
    approvals: {
      shipment: 'موافقة الشحن',
      payment: 'موافقات الدفع',
      returns: 'موافقات المرتجع',
    },
    store: {
      inScan: 'مسح وارد',
      outScan: 'مسح صادر',
      deliveryScan: 'مسح التسليم',
      bulkInScan: 'مسح وارد جماعي',
      bulkOutScan: 'مسح صادر جماعي',
      pickupScan: 'مسح الاستلام',
    },
    counters: {
      counterNos: 'أرقام العدادات',
      changeStoreRef: 'تغيير مرجع المتجر',
      areaStoreNoLock: 'قفل رقم المتجر للمنطقة',
      updateStoreRefNo: 'تحديث رقم مرجع المتجر',
    },
    customization: {
      driverCommission: 'عمولة السائق',
      clientAreaCharges: 'رسوم المناطق للعميل',
      voucherBook: 'دفتر السندات',
      smsConfiguration: 'إعداد الرسائل القصيرة',
      whatsappConfiguration: 'إعداد واتساب',
      documentNumber: 'رقم المستند',
      status: 'الحالة',
      userPageRole: 'صلاحيات صفحات المستخدم',
      printHeader: 'ترويسة الطباعة',
    },
    user: {
      menuTitle: 'الحساب',
      profile: 'الملف الشخصي',
      companyProfile: 'ملف الشركة',
      changePassword: 'تغيير كلمة المرور',
      licenseInfo: 'معلومات الترخيص',
    },
    page: {
      dashboard: 'لوحة التحكم',
      vehicleDashboard: 'لوحة المركبات',
      createOrderHeading: 'إدخال الطلب',
      createOrderSub: 'إنشاء وإدارة الطلبات',
      orderList: 'قائمة الطلبات',
      adminOrderEdit: 'تعديل الطلب',
      adminOrderPaymentEdit: 'تعديل دفع الطلب',
      adminOrderStatusEdit: 'تعديل حالة الطلب',
      adminOrderDateEdit: 'تعديل تاريخ الطلب',
      adminOrderLocationEdit: 'تعديل موقع الطلب',
      adminOrderAdjustment: 'تعديل الطلبات',
      todaysOrders: 'طلبات اليوم',
      deliveryAllocation: 'توزيع التسليم',
      deliveryAllocationAllocate: 'توزيع الطلبات',
      deliveryNotes: 'مذكرات التسليم',
      deliveryStatistics: 'إحصائيات التسليم',
      deliveryExchange: 'تبديل التسليم',
      pushOrder: 'دفع الطلب',
      pushOrderSub: 'دفع الطلبات إلى إيصالات التسليم',
      pickupAllocation: 'توزيع الاستلام',
      pickupAllocationAllocate: 'توزيع الطلبات',
      pickupNotes: 'مذكرات الاستلام',
      pickupStatus: 'حالة الاستلام',
      pickupStatistics: 'إحصائيات الاستلام',
      pickupExchange: 'تبديل الاستلام',
      pushPickup: 'دفع الاستلام',
      pushPickupSub: 'دفع الطلبات إلى إيصالات الاستلام',
      pickupNote: 'مذكرة الاستلام',
      newShipment: 'شحنة جديدة',
      shipmentList: 'قائمة الشحنات',
      createPayment: 'إنشاء دفعة',
      clientPayments: 'مدفوعات العملاء',
      pendingPayments: 'المدفوعات المعلقة',
      clientPaymentStatus: 'حالة دفع العميل',
      createReturn: 'إنشاء مرتجع',
      clientReturns: 'مرتجعات العملاء',
      pendingReturns: 'المرتجعات المعلقة',
      clientReturnStatus: 'حالة مرتجع العميل',
      shipmentApprovals: 'موافقة الشحن',
      paymentApprovalsList: 'موافقات الدفع',
      returnApprovalsList: 'موافقات المرتجع',
      deliveryNote: 'مذكرة التسليم',
      newRef: 'جديد',
    },
    tabs: {
      orderDetails: 'تفاصيل الطلب',
      editEmployee: 'تعديل موظف',
      editClient: 'تعديل عميل',
      editCustomer: 'تعديل زبون',
      editAgent: 'تعديل وكيل',
      editVehicle: 'تعديل مركبة',
      vehicleDetails: 'تفاصيل المركبة',
      editMaintenance: 'تعديل الصيانة',
      editDocument: 'تعديل مستند',
      editInsurance: 'تعديل التأمين',
      editAssignment: 'تعديل التعيين',
      editFine: 'تعديل المخالفة',
      vehicleFuelLogs: 'سجل وقود المركبات',
      vehiclePermits: 'تصاريح المركبات',
      vehicleInspections: 'فحوصات المركبات',
      vehicleExpenses: 'مصاريف المركبات',
      vehicleOdometerLogs: 'سجل عداد المركبات',
      vehicleAccidents: 'حوادث المركبات',
      vehicleServiceHistory: 'سجل خدمة المركبات',
    },
  },
};

/** Full path -> dot key under bundles (for tabs + sidebar items). */
export const PAGE_PATH_TO_NAV_KEY = {
  '/home': 'home.dashboard',
  '/home/dashboards/vehicle': 'home.vehicleDashboard',
  '/home/masters/emirates': 'masters.emirates',
  '/home/masters/areas': 'masters.areas',
  '/home/masters/locations': 'masters.locations',
  '/home/masters/expenses': 'masters.expenses',
  '/home/masters/reasons': 'masters.reasons',
  '/home/masters/types': 'masters.types',
  '/home/masters/vehicles': 'masters.vehicles',
  '/home/masters/designations': 'masters.designations',
  '/home/tracking/voucher': 'tracking.voucher',
  '/home/tracking/bill': 'tracking.bill',
  '/home/tracking/customer': 'tracking.customer',
  '/home/tracking/driver': 'tracking.driver',
  '/home/tracking/activity-tracker': 'tracking.activityTracker',
  '/home/accounts/employees': 'accounts.employees',
  '/home/accounts/addEmployee': 'accounts.addEmployee',
  '/home/accounts/clients': 'accounts.clients',
  '/home/accounts/addClient': 'accounts.addClient',
  '/home/accounts/customers': 'accounts.customers',
  '/home/accounts/addCustomer': 'accounts.addCustomer',
  '/home/accounts/agents': 'accounts.agents',
  '/home/accounts/addAgent': 'accounts.addAgent',
  '/home/accounts/users': 'accounts.users',
  '/home/vehicle-management/vehicle-list': 'vehicle.list',
  '/home/vehicle-management/assignments': 'vehicle.assignments',
  '/home/vehicle-management/maintenance': 'vehicle.maintenance',
  '/home/vehicle-management/fuel-logs': 'vehicle.fuelLogs',
  '/home/vehicle-management/documents': 'vehicle.documents',
  '/home/vehicle-management/insurance': 'vehicle.insurance',
  '/home/vehicle-management/permits': 'vehicle.permits',
  '/home/vehicle-management/inspections': 'vehicle.inspections',
  '/home/vehicle-management/vehicle-expenses': 'vehicle.expenses',
  '/home/vehicle-management/odometer-logs': 'vehicle.odometerLogs',
  '/home/vehicle-management/accidents': 'vehicle.accidents',
  '/home/vehicle-management/service-history': 'vehicle.serviceHistory',
  '/home/vehicle-management/fine-records': 'vehicle.fineRecords',
  '/home/vehicle-management/add-edit-vehicle': 'vehicle.addEdit',
  '/home/vehicle-management/add-maintenance': 'vehicle.addMaintenance',
  '/home/vehicle-management/add-documents': 'vehicle.addDocument',
  '/home/vehicle-management/add-insurance': 'vehicle.addInsurance',
  '/home/vehicle-management/add-assignment': 'vehicle.addAssignment',
  '/home/vehicle-management/add-fine': 'vehicle.addFine',
  '/home/messages/draft-emails': 'messages.draftEmails',
  '/home/messages/draft-sms': 'messages.draftSms',
  '/home/messages/delivery-note-sms': 'messages.deliveryNoteSms',
  '/home/orders/hold-orders': 'orders.holdOrders',
  '/home/orders/reverse-cancel': 'orders.reverseCancel',
  '/home/orders/batch-print': 'orders.batchPrint',
  '/home/followup/delivery-order': 'followup.deliveryOrder',
  '/home/followup/pickup-order': 'followup.pickupOrder',
  '/home/followup/driver': 'followup.driver',
  '/home/followup/payment': 'followup.payment',
  '/home/followup/return': 'followup.return',
  '/home/expenses/driver-expense': 'expenses.driver',
  '/home/expenses/company-expense': 'expenses.company',
  '/home/counters/counter-nos': 'counters.counterNos',
  '/home/counters/change-store-ref': 'counters.changeStoreRef',
  '/home/counters/area-store-no-lock': 'counters.areaStoreNoLock',
  '/home/counters/update-store-ref-no': 'counters.updateStoreRefNo',
  '/home/customization/driver-commission': 'customization.driverCommission',
  '/home/customization/client-area-charges': 'customization.clientAreaCharges',
  '/home/customization/voucher-book': 'customization.voucherBook',
  '/home/customization/sms-configuration': 'customization.smsConfiguration',
  '/home/customization/whatsapp-configuration': 'customization.whatsappConfiguration',
  '/home/customization/document-number': 'customization.documentNumber',
  '/home/customization/status': 'customization.status',
  '/home/customization/user-page-role': 'customization.userPageRole',
  '/home/customization/print-header': 'customization.printHeader',
  '/home/user/profile': 'user.profile',
  '/home/user/company-profile': 'user.companyProfile',
  '/home/user/change-password': 'user.changePassword',
  '/home/license-info': 'user.licenseInfo',
  '/home/uploads/order-upload': 'uploads.orderUpload',
  '/home/uploads/location-upload': 'uploads.locationUpload',
  '/home/uploads/client-upload': 'uploads.clientUpload',
  '/home/approvals/shipment-approvals': 'approvals.shipment',
  '/home/approvals/payment-approvals': 'approvals.payment',
  '/home/approvals/return-approvals': 'approvals.returns',
  '/home/store/in-scan': 'store.inScan',
  '/home/store/out-scan': 'store.outScan',
  '/home/store/delivery-scan': 'store.deliveryScan',
  '/home/store/bulk-in-scan': 'store.bulkInScan',
  '/home/store/bulk-out-scan': 'store.bulkOutScan',
  '/home/store/pickup-scan': 'store.pickupScan',
  '/home/orders/order-list': 'orders.orderList',
  '/home/orders/order-calendar': 'orders.orderCalendar',
  '/home/orders/create-order': 'orders.createOrder',
  '/home/orders/todays-orders': 'orders.todaysOrders',
  '/home/pickup/allocation': 'pickup.allocation',
  '/home/pickup/notes': 'pickup.notes',
  '/home/pickup/status': 'pickup.status',
  '/home/pickup/statistics': 'pickup.statistics',
  '/home/pickup/exchange': 'pickup.exchange',
  '/home/pickup/push-pickup': 'pickup.pushPickup',
  '/home/delivery/allocation': 'delivery.allocation',
  '/home/delivery/notes': 'delivery.notes',
  '/home/delivery/statistics': 'delivery.statistics',
  '/home/delivery/exchange': 'delivery.exchange',
  '/home/delivery/push-order': 'delivery.pushOrder',
  '/home/shipments/new-shipment': 'shipments.newShipment',
  '/home/shipments/shipment-list': 'shipments.shipmentList',
  '/home/shipments/convert-shipment': 'shipments.convertShipment',
  '/home/payments/create-payment': 'payments.createPayment',
  '/home/payments/client-payments': 'payments.clientPayments',
  '/home/payments/pending-payments': 'payments.pendingPayments',
  '/home/returns/create-return': 'returns.createReturn',
  '/home/returns/client-returns': 'returns.clientReturns',
  '/home/returns/pending-returns': 'returns.pendingReturns',
  '/home/admin/order-edit': 'admin.orderEdit',
  '/home/admin/order-payment-edit': 'admin.orderPaymentEdit',
  '/home/admin/order-status-edit': 'admin.orderStatusEdit',
  '/home/admin/order-date-edit': 'admin.orderDateEdit',
  '/home/admin/order-location-edit': 'admin.orderLocationEdit',
  '/home/admin/order-adjustment': 'admin.orderAdjustment',
  '/home/accounting/account-types': 'accounting.accountTypes',
  '/home/accounting/chart-of-accounts': 'accounting.chartOfAccounts',
  '/home/accounting/vouchers/cpv': 'accounting.cpv',
  '/home/accounting/vouchers/crv': 'accounting.crv',
  '/home/accounting/vouchers/fpv': 'accounting.fpv',
  '/home/accounting/vouchers/frv': 'accounting.frv',
  '/home/accounting/vouchers/cheque-pv': 'accounting.chequePv',
  '/home/accounting/vouchers/cheque-rv': 'accounting.chequeRv',
  '/home/accounting/pdc/pending': 'accounting.pendingPdc',
  '/home/accounting/pdc/processed': 'accounting.processedPdc',
  '/home/accounting/general-ledger': 'accounting.generalLedger',
  '/home/accounting/trial-balance': 'accounting.trialBalance',
  '/home/accounting/balance-sheet': 'accounting.balanceSheet',
  '/home/accounting/profit-loss': 'accounting.profitLoss',
};

export function getNavT(lang) {
  const bundle = bundles[lang] || bundles.en;
  return (dotPath) => {
    const v = resolveDot(bundle, dotPath);
    return typeof v === 'string' ? v : dotPath;
  };
}

export function getTabLabelForPath(lang, path) {
  const key = PAGE_PATH_TO_NAV_KEY[path];
  if (key) return getNavT(lang)(key);
  if (REPORT_PATH_LABELS && REPORT_PATH_LABELS[path]) {
    if (lang === 'ar' && REPORT_PATH_AR[path]) return REPORT_PATH_AR[path];
    return REPORT_PATH_LABELS[path];
  }
  return null;
}

export function getPageHeading(lang, pageKey) {
  return getNavT(lang)(`page.${pageKey}`);
}

const STATIC_ORDER_PATHS = new Set([
  '/home/orders/create-order',
  '/home/orders/order-list',
  '/home/orders/order-calendar',
  '/home/orders/todays-orders',
]);

/**
 * Tab / sidebar label for a path, including dynamic routes not in {@link PAGE_PATH_TO_NAV_KEY}.
 */
export function getResolvedTabLabel(lang, path) {
  const direct = getTabLabelForPath(lang, path);
  if (direct) return direct;

  const t = getNavT(lang);

  if (path.startsWith('/home/orders/') && !STATIC_ORDER_PATHS.has(path)) {
    return t('tabs.orderDetails');
  }
  if (path.startsWith('/home/accounts/addEmployee/')) return t('tabs.editEmployee');
  if (path.startsWith('/home/accounts/addClient/')) return t('tabs.editClient');
  if (path.startsWith('/home/accounts/addCustomer/')) return t('tabs.editCustomer');
  if (path.startsWith('/home/accounts/addAgent/')) return t('tabs.editAgent');

  if (path.startsWith('/home/vehicle-management/add-edit-vehicle/')) return t('tabs.editVehicle');
  if (path.startsWith('/home/vehicle-management/vehicle-details/')) return t('tabs.vehicleDetails');

  if (path.startsWith('/home/vehicle-management/add-maintenance/')) return t('tabs.editMaintenance');
  if (path.startsWith('/home/vehicle-management/add-documents/')) return t('tabs.editDocument');
  if (path.startsWith('/home/vehicle-management/add-insurance/')) return t('tabs.editInsurance');
  if (path.startsWith('/home/vehicle-management/add-assignment/')) return t('tabs.editAssignment');

  if (path.match(/\/home\/vehicle-management\/add-fine\/\d+\/\d+$/)) return t('tabs.editFine');
  if (path.startsWith('/home/vehicle-management/add-fine/')) return t('vehicle.addFine');

  if (path.startsWith('/home/vehicle-management/fuel-logs/')) return t('tabs.vehicleFuelLogs');
  if (path.startsWith('/home/vehicle-management/permits/')) return t('tabs.vehiclePermits');
  if (path.startsWith('/home/vehicle-management/inspections/')) return t('tabs.vehicleInspections');
  if (path.startsWith('/home/vehicle-management/vehicle-expenses/')) return t('tabs.vehicleExpenses');
  if (path.startsWith('/home/vehicle-management/odometer-logs/')) return t('tabs.vehicleOdometerLogs');
  if (path.startsWith('/home/vehicle-management/accidents/')) return t('tabs.vehicleAccidents');
  if (path.startsWith('/home/vehicle-management/service-history/')) return t('tabs.vehicleServiceHistory');

  if (path.startsWith('/home/shipments/convert-shipment/')) return t('shipments.convertShipment');
  if (path.startsWith('/home/payments/payment-status/')) return t('page.clientPaymentStatus');
  if (path.startsWith('/home/returns/return-status/')) return t('page.clientReturnStatus');

  if (path.startsWith('/home/accounting/vouchers/')) {
    const parts = path.split('/').filter(Boolean);
    const slug = parts[3];
    const tail = parts[4];
    const voucherKey = {
      cpv: 'accounting.cpv',
      crv: 'accounting.crv',
      fpv: 'accounting.fpv',
      frv: 'accounting.frv',
      'cheque-pv': 'accounting.chequePv',
      'cheque-rv': 'accounting.chequeRv',
    }[slug];
    if (tail && voucherKey) return t(voucherKey);
  }

  const parts = path.split('/').filter(Boolean);
  const last = parts[parts.length - 1] || 'home';
  let label = last.replace(/-/g, ' ');
  if (lang === 'en' && label.length > 0) {
    label = label.charAt(0).toUpperCase() + label.slice(1);
  }
  return label;
}
